<template>
    <div class="ppv2-w-full ppv2-min-h-screen ppv2-bg-white dark:ppv2-bg-dark ppv2-flex ppv2-flex-col poppinsRegular">
        <!-- Toolbar -->
        <ToolBar
            :staffResouerceList="staffResouerceList"
            :selectedWeek="selectedWeek"
            :weekDetails="weekDetails"
            :isWeekPublished="isWeekPublished"
            :draftCount="draftCount"
            :hasPlanningData="hasPlanningData"
            :isCurrentWeek="isCurrentWeek"
            @week-before="weekBefore"
            @week-after="weekAfter"
            @current-week="currentWeek"
            @load-staff-planning="loadStaffResourcesPlanning"
            @update:selectedWeek="selectedWeek = $event"
            @export="handleExport"
            @clone="prepareClone"
            @publish="publishPlanning"
            @send="handleSendPlanning"
        />
        <!-- Content Area -->
        <div class="ppv2-flex-1 ppv2-overflow-auto ppv2-relative">
            <div
                v-if="loading"
                class="ppv2-absolute ppv2-top-6 ppv2-left-1/2 -translate-x-1/2 ppv2-z-20 ppv2-inline-flex ppv2-items-center ppv2-gap-3 ppv2-rounded-full ppv2-px-4 ppv2-py-2 ppv2-text-sm poppinsMedium ppv2-text-black ppv2-shadow ppv2-border ppv2-border-gray-200"
            >
                <div
                    class="ppv2-animate-spin ppv2-h-4 ppv2-w-4 ppv2-rounded-full ppv2-border-2 ppv2-border-black ppv2-border-opacity-70 ppv2-border-t-transparent"
                ></div>
                {{ $t('staff_planning_v3.main.loading') }}
            </div>

            <!-- Planning Table -->
            <div class="ppv2-w-full" :class="{ 'ppv2-opacity-50 ppv2-pointer-events-none': loading }">
                <table class="ppv2-w-full ppv2-min-w-[1200px] ppv2-text-left ppv2-border-collapse ppv2-table-fixed">
                    <thead>
                        <tr class="ppv2-sticky ppv2-top-0 ppv2-z-30">
                            <th
                                class="ppv2-sticky ppv2-left-0 ppv2-text-center ppv2-z-40 ppv2-bg-[#b775ff14] ppv2-border-r ppv2-border-b ppv2-border-white ppv2-w-[220px] ppv2-py-3 ppv2-px-4"
                            >
                                <div class="ppv2-group ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2">
                                    <span class="ppv2-text-xs poppinsBold ppv2-text-black ppv2-uppercase ppv2-tracking-wide">
                                        {{ $t('staff_planning_v3.main.team_title') }}
                                    </span>

                                    <button
                                        @click="createNewStaff"
                                        class="ppv2-absolute ppv2-opacity-0 ppv2-pointer-events-none group-hover:ppv2-opacity-100 group-hover:ppv2-pointer-events-auto group-hover:ppv2-relative ppv2-transition-all duration-200 hover:ppv2-scale-110 ppv2-w-6 ppv2-h-6 ppv2-rounded-full ppv2-bg-[#8B5CF6] ppv2-text-white ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-sm ppv2-leading-none hover:ppv2-bg-[#7c3aed] ppv2-transition-colors"
                                    >
                                        <Plus :size="16" />
                                    </button>
                                </div>
                            </th>

                            <th
                                v-for="(day, index) in getDaysOfWeek()"
                                :key="index"
                                class="ppv2-border-r ppv2-border-b ppv2-border-white ppv2-py-2 ppv2-text-center ppv2-bg-[#b775ff14] !ppv2-py-1"
                            >
                                <div class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-80 ppv2-uppercase ppv2-tracking-wide">
                                    {{ getDayName(index) }}
                                </div>

                                <div class="ppv2-text-lg poppinsBold ppv2-text-black ppv2-text-opacity-80 ppv2-mt-0.5">
                                    {{ getDayDate(index) }}
                                </div>
                            </th>

                            <th
                                class="ppv2-w-[80px] ppv2-bg-[#b775ff14] ppv2-border-b ppv2-border-white ppv2-py-3 ppv2-text-center ppv2-text-[10px] poppinsBold ppv2-text-black ppv2-text-opacity-90 ppv2-uppercase ppv2-tracking-wide"
                            >
                                {{ $t('staff_planning_v3.main.total') }}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <PlanningRow
                            v-for="staff in filteredStaffPlanning"
                            :key="staff.user?.id ?? staff.uid"
                            :item="staff"
                            :days-of-week="daysOfWeek"
                            :menu-items="menuItemsFor(staff)"
                            :get-day-data="getDayData"
                            :format-day-content="formatDayContent"
                            :get-cell-style="getCellStyle"
                            @edit="handleEditCell($event.item, $event.index)"
                            @delete="
                                showDeleteConfirmModal = true
                                deleteTarget = { staff, dayIndex: $event.index }
                            "
                            @create-shift="prepareShift($event.item, $event.index)"
                            @create-absence="prepareAbsence($event.item, $event.index)"
                            @menu-select="onMenuSelect($event, staff)"
                        />
                        <tr>
                            <td
                                class="ppv2-sticky ppv2-left-0 ppv2-z-10 ppv2-border-r ppv2-border-b ppv2-border-gray-100 ppv2-h-[72px] ppv2-bg-[#b775ff14]"
                            >
                                <div class="ppv2-group ppv2-flex ppv2-items-center ppv2-justify-center ppv2-h-full">
                                    <Tooltip :text="$t('staff_planning_v3.row.add_entry')" position="top">
                                        <button
                                            @click="openResourceModal"
                                            class="ppv2-opacity-0 ppv2-pointer-events-none group-hover:ppv2-opacity-100 group-hover:ppv2-pointer-events-auto ppv2-transition-all duration-200 hover:ppv2-scale-110 ppv2-w-6 ppv2-h-6 ppv2-rounded-full ppv2-bg-[#8B5CF6] ppv2-text-white ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-sm ppv2-leading-none hover:ppv2-bg-[#7c3aed] ppv2-transition-colors"
                                        >
                                            <Plus :size="16" />
                                        </button>
                                    </Tooltip>
                                </div>
                            </td>
                            <td class="ppv2-border-b ppv2-border-gray-100 ppv2-h-[72px]"></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <AbsenceModal
            v-model="showAbsenceModal"
            :absenceForm="absenceForm"
            :staffResouerceList="staffResouerceList"
            :selectedAbsenceStaff="selectedAbsenceStaff"
            :isEditingAbsence="isEditingAbsence"
            @save="saveAbsence($event)"
        />

        <!-- Create Shift Modal -->
        <CreateShift
            v-model="showShiftModal"
            :shift-form="shiftForm"
            :staffResouerceList="staffResouerceList"
            :selectedWeek="selectedWeek"
            :is-editing-shift="isEditingShift"
            :show-employee-select="showEmployeeSelect"
            @save="saveShift"
            :show-recurrence-accordion="showRecurrenceAccordion"
            @update:showRecurrenceAccordion="showRecurrenceAccordion = $event"
            @update:showCopyAccordion="showCopyAccordion = $event"
        />
        <!-- Delete Confirmation Popup -->
        <CustomDialog
            v-model="showDeleteConfirmModal"
            :defaultTitle="deletePopupTitle"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeleteConfirmModal = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
        <!-- Download Planning Modal -->
        <DownloadPlanningModal
            v-model="showDownloadModal"
            :download-form="downloadForm"
            :next-month-weeks="nextMonthWeeks"
            :personalized-weeks="personalizedWeeks"
            :modal-staff-list="modalStaffList('download')"
            :can-show-month="canShowMonth"
            :selectedWeek="selectedWeek"
            @download="downloadPlanning"
            :enabled-weeks-month-count="enabledWeeksMonthCount"
            :enabled-weeks-perso-count="enabledWeeksPersoCount"
        />
        <!-- Copy Planning Modal -->
        <CopyPlanningModal
            v-model="showCopyModal"
            :staffResouerceList="staffResouerceList"
            :available-weeks="personalizedWeeks"
            :selectedWeek="selectedWeek"
            :initial-staff-id="copyModalInitialStaffId"
            :show-source-selection="copyFromDuplicate"
            @confirm="onCopyModalConfirm"
        />

        <CustomDialog
            v-model="showCopyOverwriteConfirmModal"
            :defaultTitle="$t('staff_planning_v3.main.confirm_overwrite_title')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('staff_planning_v3.main.confirm_overwrite_desc') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="showCopyOverwriteConfirmModal = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="executeCopyMultiple"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('staff_planning_v3.copy_modal.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <SendPlanningModal
            v-model="showSendModal"
            :send-form="sendForm"
            :next-month-weeks="nextMonthWeeks"
            :personalized-weeks="personalizedWeeks"
            :modal-staff-list="modalStaffList('send')"
            :can-show-month="canShowMonth"
            :selectedWeek="selectedWeek"
            @send="sendPlanning"
            :enabled-weeks-month-count="enabledWeeksMonthCount"
            :enabled-weeks-perso-count="enabledWeeksPersoCount"
        />
        <ResourceExterneModal
            v-model="showResourceExterneModal"
            :resource-form="selectedResource"
            :is-editing="isEditingResource"
            @save="isEditingResource ? handleUpdateResource($event) : handleSaveResource($event)"
            :errors="resourceErrors"
        />
        <ConfirmDeleteWeek
            v-model="showConfirmDeleteWeek"
            :title="deletePopupTitle"
            :description="deletePopupDescription"
            @confirm="confirmDeleteWeek"
        />
        <CustomDialog
            v-model="confirmDeleteResourceModal"
            :defaultTitle="$t('staff_planning_v3.main.confirm_delete_resource_title')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('staff_planning_v3.main.confirm_delete_resource_desc') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="confirmDeleteResourceModal = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDeleteResource"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import BaseButton from '../../Shared/BaseButton.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import Tooltip from '../../Shared/Tooltip.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import ToolBar from './components/Toolbar.vue'
import PlanningRow from './components/PlanningRow.vue'
import AbsenceModal from './components/AbsenceModal.vue'
import CreateShift from './components/CreateShift.vue'
import DownloadPlanningModal from './components/DownloadPlanningModal.vue'
import SendPlanningModal from './components/SendPlanningModal.vue'
import CopyPlanningModal from './components/CopyPlanningModal.vue'
import ResourceExterneModal from './components/ResourceExterneModal.vue'
import ConfirmDeleteWeek from './components/ConfirmDeleteWeek.vue'
import { Copy, Pencil, Plus, Send, Trash2 } from 'lucide-vue'
export default {
    name: 'PlanningStaffsV3',
    props: {
        token: String,
    },
    components: {
        BaseButton,
        CustomDialog,
        Plus,
        Tooltip,
        ToolBar,
        AbsenceModal,
        CreateShift,
        DownloadPlanningModal,
        SendPlanningModal,
        CopyPlanningModal,
        ResourceExterneModal,
        PlanningRow,
        Trash2,
        ConfirmDeleteWeek,
    },
    data() {
        return {
            selectedWeek: this.getCurrentWeek(),
            staffPlanning: [],
            downloadStaffTotals: [],
            staffList: [],
            loading: false,
            cloneSourceWeek: this.getCurrentWeek(),
            cloneSelectedStaffs: [],
            daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
            selectedAbsenceStaff: null,
            selectedAbsenceDayIndex: null,
            selectedAbsenceDateFormated: '',
            showAbsenceModal: false,
            showShiftModal: false,
            showRecurrenceAccordion: false,
            showCopyAccordion: false,
            isEditingAbsence: false,
            isEditingShift: false,
            showEmployeeSelect: false,
            absenceForm: {
                staff_id: '',
                motif: 'Congé payé',
                date: '',
                start_time: '16:00',
                end_time: '20:00',
                comment: '',
                frequency: 'Toutes les semaines',
                recurrence_end_date: '',
            },
            shiftForm: {
                user_id: '',
                employee_name: '',
                date: '',
                periods: [{ start: '16:00', end: '20:00' }],
                note: '',
                frequency: 'Toutes les semaines',
                recurrence_end_date: null,
                copy_from_staff_id: '',
                copy_from_week: '',
                recurrence_days: [],
            },
            days: [
                { id: 'Monday', label: this.$t('experience.timeSpecific.days.mon') },
                { id: 'Tuesday', label: this.$t('experience.timeSpecific.days.tue') },
                { id: 'Wednesday', label: this.$t('experience.timeSpecific.days.wed') },
                { id: 'Thursday', label: this.$t('experience.timeSpecific.days.thu') },
                { id: 'Friday', label: this.$t('experience.timeSpecific.days.fri') },
                { id: 'Saturday', label: this.$t('experience.timeSpecific.days.sat') },
                { id: 'Sunday', label: this.$t('experience.timeSpecific.days.sun') },
            ],
            multiple: true,
            showDeleteConfirmModal: false,
            deletePopupTitle: this.$t('staff_planning_v3.main.confirm_delete_title'),
            deletePopupDescription: this.$t('staff_planning_v3.main.confirm_shift_delete_desc'),
            deleteTarget: null,
            showDownloadModal: false,
            downloadForm: {
                type: 'week',
                staffs: [],
                note: '',
                weekStr: '',
                weeks: [],
            },
            sendForm: {
                type: 'week',
                staffs: [],
                note: '',
                weekStr: '',
                weeks: [],
            },
            nextMonthWeeks: [], //use in dowload choose month
            personalizedWeeks: [], //use in dowload all month
            publishedUpcomingWeeks: [], // ISO week strings (YYYY-Www) that are published and upcoming
            showSendModal: false,
            showResourceExterneModal: false,
            isEditingResource: false,
            selectedResource: { name: '', email: '', uid: '' },
            resourceExternes: [],
            enabledWeeksMonthCount: 0,
            enabledWeeksPersoCount: 0,
            resourceErrors: {},
            confirmDeleteResourceModal: false,
            resourceToDelete: null,
            /* use dropdown select delete week*/
            showConfirmDeleteWeek: false,
            targetRow: null,
            /*--end */
            showCopyModal: false,
            showCopyOverwriteConfirmModal: false,
            copyModalInitialStaffId: null,
            copyModalPendingData: null,
            copyFromDuplicate: false,
        }
    },
    watch: {
        'downloadForm.type': {
            immediate: true,
            handler(val) {
                this.handleTypeChange(val, 'download')
            },
        },

        'sendForm.type': {
            immediate: true,
            handler(val) {
                this.handleTypeChange(val, 'send')
            },
        },

        'downloadForm.weeks': {
            deep: true,
            async handler(val) {
                await this.handleWeeksChange(val, 'download')
            },
        },
        'sendForm.weeks': {
            deep: true,
            async handler(val) {
                await this.handleWeeksChange(val, 'send')
            },
        },
        showDownloadModal: {
            immediate: true,
            handler(val) {
                if (val) this.initTypes('download')
            },
        },

        showSendModal: {
            immediate: true,
            handler(val) {
                if (val) this.initTypes('send')
            },
        },

        showCopyModal(val) {
            // Only reset copyFromDuplicate if the modal was closed without confirming
            // (i.e., the overwrite confirm modal is NOT about to open)
            if (!val && !this.showCopyOverwriteConfirmModal) {
                this.copyFromDuplicate = false
            }
        },
    },
    computed: {
        canShowMonth() {
            return this.publishedUpcomingWeeks.length >= 1
        },
        filteredStaffPlanning() {
            return this.staffPlanning || []
        },
        draftCount() {
            let count = 0
            for (const item of this.staffPlanning || []) {
                for (let i = 0; i < 7; i++) {
                    const dayData = item?.[i]
                    if (!dayData || typeof dayData !== 'object') continue
                    if (dayData.draft === 1 || dayData.draft === true) {
                        count++
                    }
                }
            }
            return count
        },
        hasPlanningData() {
            for (const item of this.staffPlanning || []) {
                for (let i = 0; i < 7; i++) {
                    const dayData = item?.[i]
                    if (dayData && typeof dayData === 'object') return true
                }
            }
            return false
        },
        isWeekPublished() {
            return this.hasPlanningData && this.draftCount === 0
        },
        weekDetails() {
            const [year, week] = this.selectedWeek.split('-W')
            return { year: parseInt(year), week: parseInt(week) }
        },
        isCurrentWeek() {
            return this.selectedWeek === this.getCurrentWeek()
        },
        emptyRowsCount() {
            const minRows = 10
            return Math.max(0, minRows - this.staffPlanning.length)
        },

        copyWeekOptions() {
            const options = []

            const [currentYear, currentWeekStr] = this.selectedWeek.split('-W')

            let year = parseInt(currentYear)

            // Add all weeks from week 1 to week 52 for the current year
            for (let week = 1; week <= 52; week++) {
                options.push({
                    value: `${year}-W${String(week).padStart(2, '0')}`,
                    label: `${this.$t('staff_planning_v3.components.create_shift.week')} ${week}`,
                })
            }

            return options
        },
        weekMenuItems() {
            return [
                {
                    label: this.$t('staff_planning_v3.main.copy_week_to'),
                    icon: Copy,
                    action: this.copyWeek,
                },
                {
                    label: this.$t('staff_planning_v3.main.send_week'),
                    icon: Send,
                    action: this.sendWeek,
                },
                {
                    label: this.$t('staff_planning_v3.main.delete_week'),
                    icon: Trash2,
                    action: this.deleteWeek,
                    danger: true,
                },
            ]
        },
        resourceMenuItems() {
            return [
                {
                    label: this.$t('staff.external_resource.edit'),
                    icon: Pencil,
                    action: this.editResource,
                },
                {
                    label: this.$t('staff.external_resource.delete'),
                    icon: Trash2,
                    action: this.requestDeleteResource,
                    danger: true,
                },
            ]
        },
        staffResouerceList() {
            const staffOptions = (this.staffPlanning || []).map((item) => {
                const isUser = item.user ? true : false

                return {
                    value: isUser ? item.user.id : item.uid,
                    label: isUser ? `${item.user.first_name ?? ''} ${item.user.last_name ?? ''}`.trim() : item.name,
                    type: isUser ? 'staff' : 'ressource externe',
                    selected: true,
                    total: item.total,
                }
            })

            return staffOptions
        },
    },
    mounted() {
        this.loadStaffResourcesPlanning()
    },
    methods: {
        menuItemsFor(staff) {
            const week = staff.total && staff.total !== '0,0h' ? this.weekMenuItems : []
            return staff.user ? week : [...week, ...this.resourceMenuItems]
        },
        normalizeFontSize,
        initTypes(type = '') {
            if (type === 'download') {
                this.handleTypeChange('month', 'download')
                this.handleTypeChange('pers', 'download')
            } else if (type === 'send') {
                this.handleTypeChange('month', 'send')
                this.handleTypeChange('pers', 'send')
            } else {
                this.handleTypeChange(this.downloadForm.type, 'download')
                this.handleTypeChange(this.sendForm.type, 'send')
            }
        },
        async cloneSinglePlanning() {
            const staffId = this.shiftForm.copy_from_staff_id
            const weekVal = this.shiftForm.copy_from_week
            const targetStaffId = this.shiftForm.user_id

            if (!staffId || !weekVal) {
                this.$toast.error(this.$t('staff_planning_v3.main.select_source'))
                return
            }

            if (!targetStaffId) {
                this.$toast.error(this.$t('staff_planning_v3.main.select_target'))
                return
            }

            this.loading = true
            try {
                const response = await fetch('/back-office/staff/planning/clone_single', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                    body: JSON.stringify({
                        source_staff_id: staffId,
                        source_week: weekVal,
                        target_staff_id: targetStaffId,
                        target_week: this.selectedWeek,
                    }),
                })

                if (!response.ok) {
                    throw new Error('Failed to clone planning')
                }

                this.$toast.success(this.$t('staff_planning_v3.main.planning_cloned'))
                this.showShiftModal = false
                await this.loadStaffResourcesPlanning()
            } catch (error) {
                console.error('Error cloning planning:', error)
                this.$toast.error(this.$t('staff_planning_v3.main.copy_error'))
            } finally {
                this.loading = false
            }
        },
        async applyCopyFromPlanning() {
            const staffId = this.shiftForm.copy_from_staff_id
            const weekVal = this.shiftForm.copy_from_week
            if (!staffId || !weekVal) return

            try {
                const params = new URLSearchParams({ week_number: weekVal })
                const response = await fetch(`/back-office/v3/planning/staff_resources?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                })

                if (!response.ok) throw new Error('Failed to fetch planning')

                const data = await response.json()
                const allStaff = data.planning || []

                // Find the selected employee in the returned data
                const sourceStaff = allStaff.find((s) => (s.user?.id || s.id) === staffId)
                if (!sourceStaff) {
                    this.$toast.error(this.$t('staff_planning_v3.main.no_planning_found'))
                    return
                }

                // Determine the day index from the current shiftForm.date
                const formDate = new Date(this.shiftForm.date)
                let dayIndex = formDate.getDay() - 1
                if (dayIndex < 0) dayIndex = 6 // Sunday = 6

                const dayData = sourceStaff[dayIndex]
                if (!dayData || typeof dayData !== 'object' || !dayData.start) {
                    this.$toast.warning(this.$t('staff_planning_v3.main.no_shift_found'))
                    return
                }

                // Build periods from the source shift
                let periods = []
                const hasSplit =
                    dayData.lunch_start &&
                    dayData.lunch_end &&
                    dayData.lunch_start !== dayData.end &&
                    dayData.lunch_end !== dayData.end

                if (hasSplit) {
                    periods = [
                        { start: dayData.start.slice(0, 5), end: dayData.lunch_start.slice(0, 5) },
                        { start: dayData.lunch_end.slice(0, 5), end: dayData.end.slice(0, 5) },
                    ]
                } else {
                    periods = [{ start: dayData.start.slice(0, 5), end: dayData.end.slice(0, 5) }]
                }

                this.shiftForm.periods = periods
                if (dayData.note) {
                    this.shiftForm.note = dayData.note
                }

                this.$toast.success(this.$t('staff_planning_v3.main.hours_copied'))
            } catch (error) {
                console.error('Error copying planning:', error)
                this.$toast.error(this.$t('staff_planning_v3.main.copy_error'))
            }
        },
        currentWeek() {
            this.selectedWeek = this.getCurrentWeek()
            this.loadStaffResourcesPlanning()
        },
        getCurrentWeek() {
            const now = new Date()
            const year = now.getFullYear()
            const week = String(this.getWeekNumber(now)).padStart(2, '0')
            return `${year}-W${week}`
        },
        getWeekNumber(date) {
            const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
            const dayNum = d.getUTCDay() || 7
            d.setUTCDate(d.getUTCDate() + 4 - dayNum)
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
            return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
        },
        weekBefore() {
            const [year, weekStr] = this.selectedWeek.split('-W')
            let week = parseInt(weekStr)
            let currentYear = parseInt(year)

            if (week === 1) {
                currentYear--
                week = this.getIsoWeeksInYear(currentYear)
            } else {
                week--
            }

            this.selectedWeek = `${currentYear}-W${String(week).padStart(2, '0')}`
            this.loadStaffResourcesPlanning()
        },
        weekAfter() {
            const [year, weekStr] = this.selectedWeek.split('-W')
            let week = parseInt(weekStr)
            let currentYear = parseInt(year)
            const maxWeeks = this.getIsoWeeksInYear(currentYear)

            if (week >= maxWeeks) {
                week = 1
                currentYear++
            } else {
                week++
            }

            this.selectedWeek = `${currentYear}-W${String(week).padStart(2, '0')}`
            this.loadStaffResourcesPlanning()
        },
        getIsoWeeksInYear(year) {
            const date = new Date()
            date.setFullYear(year, 11, 28)
            return this.getWeekNumber(date)
        },
        getDaysOfWeek() {
            return [0, 1, 2, 3, 4, 5, 6]
        },
        getDayName(index) {
            const names = [
                this.$t('resource_planning.week_days.monday'),
                this.$t('resource_planning.week_days.tuesday'),
                this.$t('resource_planning.week_days.wednesday'),
                this.$t('resource_planning.week_days.thursday'),
                this.$t('resource_planning.week_days.friday'),
                this.$t('resource_planning.week_days.saturday'),
                this.$t('resource_planning.week_days.sunday'),
            ]
            return names[index]
        },
        getDayDate(index) {
            const [year, weekStr] = this.selectedWeek.split('-W')
            const date = this.getDateFromWeek(parseInt(year), parseInt(weekStr), index)
            return String(date.getDate()).padStart(2, '0')
        },
        getDateFromWeek(year, week, dayIndex) {
            // Find Jan 4th of the year (which is always in ISO week 1)
            const d = new Date(year, 0, 4)
            // Get the day of the week for Jan 4th (0 = Sunday, 1 = Monday, etc.)
            let dayOfWeek = d.getDay() - 1
            if (dayOfWeek < 0) dayOfWeek = 6 // Make Monday 0, Sunday 6

            // Move back to the Monday of week 1
            d.setDate(d.getDate() - dayOfWeek)

            // Add the right number of weeks and the day index
            d.setDate(d.getDate() + (week - 1) * 7 + dayIndex)
            return d
        },
        isToday(dayIndex) {
            const today = new Date()
            const date = this.getDateFromWeek(
                parseInt(this.selectedWeek.split('-W')[0]),
                parseInt(this.selectedWeek.split('-W')[1]),
                dayIndex
            )
            return today.toDateString() === date.toDateString()
        },
        async loadStaffResourcesPlanning() {
            this.loading = true
            const [year, weekStr] = this.selectedWeek.split('-W')
            const params = new URLSearchParams()
            params.append('week_number', `${year}-W${weekStr}`)
            try {
                const response = await fetch(`/back-office/v3/planning/staff_resources?${params.toString()}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                })

                if (!response.ok) {
                    throw new Error('Failed to fetch staff data')
                }

                const data = await response.json()
                this.staffPlanning = data.planning || []
            } catch (error) {
                this.$toast.error(this.$t('staff_planning_v3.main.load_error'))
            } finally {
                this.loading = false
            }
        },
        getDayData(staff, index) {
            const exception = this.getExceptionForDay(staff, index)
            if (exception) {
                return exception
            }
            return staff[index]
        },
        getExceptionForDay(staff, dayIndex) {
            const exceptions = staff.planning_exceptions || staff.planning_exception || []
            if (!exceptions.length) return null

            const [year, weekStr] = this.selectedWeek.split('-W')
            const dateObj = this.getDateFromWeek(parseInt(year), parseInt(weekStr), dayIndex)

            // Format to YYYY-MM-DD
            const tzoffset = dateObj.getTimezoneOffset() * 60000
            const dateStr = new Date(dateObj - tzoffset).toISOString().slice(0, 10)

            const exception = exceptions.find((e) => {
                if (!e.start || !e.end) return false
                const startStr = e.start.slice(0, 10)
                const endStr = e.end.slice(0, 10)
                return startStr <= dateStr && dateStr <= endStr && parseInt(e.status) !== 4
            })

            if (exception) {
                return this.getStatusName(exception.status, exception)
            }
            return null
        },
        getStatusName(status, exception = null) {
            if (parseInt(status) === 5 && exception && exception.nature_type) {
                return exception.nature_type
            }
            const statusMap = {
                1: 'Maladie',
                2: 'Jour de vacances',
                3: 'Repos',
                4: 'Ajout manuel (Calendrier)',
                5: 'Personalisée',
            }
            return statusMap[status] || 'Non défini'
        },
        formatTime(timeStr) {
            if (!timeStr) return ''
            const parts = timeStr.split(':')
            return `${parseInt(parts[0])}:${parts[1]}`
        },
        formatDayContent(dayData) {
            if (!dayData) return '-'

            if (typeof dayData === 'string') {
                return dayData
            }

            if (dayData.start && dayData.end) {
                const endTime = dayData._merged_end || dayData.end
                if (dayData.lunch_start && dayData.lunch_end && dayData.lunch_start !== dayData.lunch_end) {
                    return (
                        `${this.formatTime(dayData.start)} - ${this.formatTime(dayData.lunch_start)}\n` +
                        `${this.formatTime(dayData.lunch_end)} - ${this.formatTime(endTime)}`
                    )
                }

                // Otherwise, just return the continuous shift
                return `${this.formatTime(dayData.start)} - ${this.formatTime(endTime)}`

                return content
            }

            return '-'
        },
        getCellStyle(dayData, dayIndex) {
            let bgColor = 'transparent'
            let color = '#374151'
            let border = '1.5px solid white'

            // Determine if the date is in the past
            const [year, weekStr] = this.selectedWeek.split('-W')
            const cellDate = this.getDateFromWeek(parseInt(year), parseInt(weekStr), dayIndex)
            cellDate.setHours(0, 0, 0, 0)
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const isPast = cellDate < today

            if (typeof dayData === 'string') {
                if (dayData === 'Absent' || dayData === 'Maladie') {
                    bgColor = 'rgba(0, 0, 0, 0.65)'
                    color = '#FFFFFF'
                } else if (dayData === 'Repos') {
                    bgColor = '#5a008711'
                } else if (dayData === 'Jour de vacances' || dayData === 'Congé payé') {
                    bgColor = '#ef9e041a'
                } else if (dayData !== 'Non défini' && dayData !== '') {
                    // Exception published
                    bgColor = isPast ? 'rgba(53, 133, 0, 0.13)' : 'rgba(32, 178, 25, 0.2)'
                    color = '#172736'
                }
            } else if (dayData && dayData.start) {
                if (dayData.locked) {
                    bgColor = 'rgba(0, 0, 0, 0.65)'
                    color = '#FFFFFF'
                } else if (dayData.draft) {
                    // draft shift
                    bgColor = 'rgba(155, 154, 154, 0.16)'
                    color = '#000'
                    border = '1.5px dotted #000'
                } else {
                    // validated/published shift
                    if (isPast) {
                        bgColor = 'rgba(53, 133, 0, 0.13)'
                    } else {
                        bgColor = 'rgba(32, 178, 25, 0.2)'
                    }
                    color = '#172736'
                }
            }

            return {
                backgroundColor: bgColor,
                color: color,
                border: border,
            }
        },
        handleEditCell(staff, index) {
            const hasException = this.getExceptionForDay(staff, index)
            if (hasException) {
                this.prepareAbsence(staff, index)
            } else {
                this.prepareShift(staff, index)
            }
        },
        async handleDeleteCell(staff, dayIndex) {
            this.showDeleteConfirmModal = false
            const hasException = this.getExceptionForDay(staff, dayIndex)

            const [year, weekStr] = this.selectedWeek.split('-W')
            const dateObj = this.getDateFromWeek(parseInt(year), parseInt(weekStr), dayIndex)

            // Format YYYY-MM-DD
            const tzoffset = dateObj.getTimezoneOffset() * 60000
            const dateStr = new Date(dateObj - tzoffset).toISOString().slice(0, 10)

            if (hasException) {
                // Delete absence / exception
                const exceptions = staff.planning_exceptions || staff.planning_exception || []

                const existingExc = exceptions.find((e) => {
                    if (!e.start || !e.end) return false

                    const start = e.start.slice(0, 10)
                    const end = e.end.slice(0, 10)

                    return start <= dateStr && dateStr <= end
                })

                if (!existingExc) return

                try {
                    await axios.delete(`/back-office/v3/planning/staff/planning-exceptions/${existingExc.id}`)

                    this.$toast?.success?.(this.$t('staff_planning_v3.main.absence_deleted'))

                    await this.loadStaffResourcesPlanning()
                } catch (error) {
                    console.error(error)

                    this.$toast?.error?.(this.$t('staff_planning_v3.main.absence_delete_error'))
                }
            } else {
                // Delete shift
                const existingShift = staff[dayIndex]

                if (!existingShift || !existingShift.id) return

                try {
                    await axios.delete(`/back-office/v3/planning/staff/planning/${existingShift.id}`)

                    this.$toast?.success?.(this.$t('staff_planning_v3.main.shift_deleted'))

                    await this.loadStaffResourcesPlanning()
                } catch (error) {
                    console.error(error)

                    this.$toast?.error?.(this.$t('staff_planning_v3.main.shift_delete_error'))
                }
            }
        },
        async confirmDelete() {
            if (!this.deleteTarget) return
            const { staff, dayIndex } = this.deleteTarget
            this.showDeleteConfirmModal = false
            this.deleteTarget = null
            await this.handleDeleteCell(staff, dayIndex)
        },
        prepareAbsence(staff, dayIndex) {
            this.showRecurrenceAccordion = false
            this.showCopyAccordion = false
            this.selectedAbsenceStaff = staff
            this.selectedAbsenceDayIndex = dayIndex

            const [year, weekStr] = this.selectedWeek.split('-W')
            const dateObj = this.getDateFromWeek(parseInt(year), parseInt(weekStr), dayIndex)
            const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
            this.selectedAbsenceDateFormated = dateObj.toLocaleDateString('fr-FR', options)

            const tzoffset = dateObj.getTimezoneOffset() * 60000
            const localISOTime = new Date(dateObj - tzoffset).toISOString().slice(0, 10)

            // Look up existing exception details
            const exceptions = staff.planning_exceptions || staff.planning_exception || []
            const dateStr = localISOTime
            const existingExc = exceptions.find((e) => {
                if (!e.start || !e.end) return false
                const start = e.start.slice(0, 10)
                const end = e.end.slice(0, 10)
                return start <= dateStr && dateStr <= end
            })

            this.isEditingAbsence = !!existingExc
            this.absenceForm = {
                staff_id: staff.uid || staff.user?.id || staff.id,
                motif: existingExc ? this.getStatusName(existingExc.status, existingExc) : 'Congé payé',
                date: localISOTime,
                periods: [
                    {
                        start:
                            existingExc && existingExc.start && existingExc.start.length > 11
                                ? existingExc.start.slice(11, 16)
                                : '08:00',
                        end:
                            existingExc && existingExc.end && existingExc.end.length > 11
                                ? existingExc.end.slice(11, 16)
                                : '18:00',
                    },
                ],
                comment: existingExc && existingExc.comment ? existingExc.comment : '',
                frequency: 'Toutes les semaines',
                recurrence_end_date: '',
                id: existingExc ? existingExc.id : null,
            }
            this.showAbsenceModal = true
        },
        prepareShift(staff, dayIndex) {
            this.showRecurrenceAccordion = false
            this.showCopyAccordion = false
            this.isEditingShift = false
            this.showEmployeeSelect = true

            const [year, weekStr] = this.selectedWeek.split('-W')
            const dateObj = this.getDateFromWeek(parseInt(year), parseInt(weekStr), dayIndex)

            // Format to YYYY-MM-DD
            const tzoffset = dateObj.getTimezoneOffset() * 60000
            const dateStr = new Date(dateObj - tzoffset).toISOString().slice(0, 10)

            const existingShift = staff[dayIndex]
            let periods = [{ start: '08:00', end: '18:00' }]
            let note = ''

            if (existingShift && typeof existingShift === 'object') {
                if (existingShift.start && existingShift.end) {
                    const displayEnd = existingShift._merged_end || existingShift.end
                    const hasSplit =
                        existingShift.lunch_start &&
                        existingShift.lunch_end &&
                        existingShift.lunch_start !== existingShift.end &&
                        existingShift.lunch_end !== existingShift.end
                    if (hasSplit) {
                        periods = [
                            { start: existingShift.start.slice(0, 5), end: existingShift.lunch_start.slice(0, 5) },
                            { start: existingShift.lunch_end.slice(0, 5), end: displayEnd.slice(0, 5) },
                        ]
                    } else {
                        periods = [{ start: existingShift.start.slice(0, 5), end: displayEnd.slice(0, 5) }]
                    }
                }
                if (existingShift.note) {
                    note = existingShift.note
                }
            }
            this.isEditingShift = !!(existingShift && typeof existingShift === 'object' && existingShift.start)
            this.shiftForm = {
                user_id: staff.uid || staff.user?.id || staff.id,
                employee_name: staff.name || (staff.user ? `${staff.user.first_name} ${staff.user.last_name}` : 'Employe'),
                date: dateStr,
                periods: periods,
                note: note,
                frequency: 'Toutes les semaines',
                recurrence_end_date: '',
                copy_from_staff_id: '',
                copy_from_week: '',
                recurrence_days: [this.getDayNameForKey(dateObj.getDay())],
            }
            this.showShiftModal = true
        },
        prepareNewShift() {
            this.showRecurrenceAccordion = false
            this.showCopyAccordion = false
            this.isEditingShift = false
            this.showEmployeeSelect = true

            // Default to Monday of the currently selected week
            const [year, weekStr] = this.selectedWeek.split('-W')
            const dateObj = this.getDateFromWeek(parseInt(year), parseInt(weekStr), 0)
            const tzoffset = dateObj.getTimezoneOffset() * 60000
            const dateStr = new Date(dateObj - tzoffset).toISOString().slice(0, 10)

            this.shiftForm = {
                user_id: '',
                employee_name: '',
                date: dateStr,
                periods: [{ start: '08:00', end: '18:00' }],
                note: '',
                frequency: 'Toutes les semaines',
                recurrence_end_date: '',
                copy_from_staff_id: '',
                copy_from_week: '',
                recurrence_days: [this.getDayNameForKey(dateObj.getDay())],
            }

            this.showShiftModal = true
        },
        createNewStaff() {
            return (window.location.href = '/back-office/v3/staffs/create')
        },
        getDayNameForKey(jsDay) {
            const dayMap = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
            return dayMap[jsDay]
        },
        addShiftPeriod() {
            if (this.shiftForm.periods.length < 2) {
                if (this.shiftForm.periods.length > 0) {
                    this.shiftForm.periods[0].start = '08:00'
                    this.shiftForm.periods[0].end = '12:00'
                }
                this.shiftForm.periods.push({ start: '14:00', end: '18:00' })
            }
        },
        removeShiftPeriod(index) {
            if (this.shiftForm.periods.length > 1) {
                this.shiftForm.periods.splice(index, 1)
            }
        },
        calculateDuration(start, end) {
            if (!start || !end) return '0h00'
            const [startH, startM] = start.split(':').map(Number)
            const [endH, endM] = end.split(':').map(Number)

            let diffMinutes = endH * 60 + endM - (startH * 60 + startM)
            if (diffMinutes < 0) diffMinutes += 24 * 60 // handle overnight shifts

            const hours = Math.floor(diffMinutes / 60)
            const minutes = diffMinutes % 60
            return `${hours}h${String(minutes).padStart(2, '0')}`
        },
        async saveShift() {
            if (this.shiftForm.copy_from_staff_id && this.shiftForm.copy_from_week) {
                await this.cloneSinglePlanning()
                return
            }

            if (!this.shiftForm.periods?.length) return

            const p0 = this.shiftForm.periods[0]

            if (!p0.start || !p0.end) {
                this.$toast.error(this.$t('staff_planning_v3.main.fill_hours'))
                return
            }

            let start = p0.start
            let end = p0.end
            let lunch_start = ''
            let lunch_end = ''

            if (this.shiftForm.periods.length > 1) {
                const p1 = this.shiftForm.periods[1]
                if (p1.start && p1.end) {
                    lunch_start = p0.end
                    lunch_end = p1.start
                    end = p1.end
                }
            }

            const userId = this.shiftForm.user_id
            if (!userId) {
                this.$toast.error(this.$t('staff_planning_v3.main.select_employee_only'))
                return
            }

            const dateObj = new Date(this.shiftForm.date)

            if (isNaN(dateObj.getTime())) {
                this.$toast.error(this.$t('staff_planning_v3.main.invalid_date'))
                return
            }

            const weekNumber = this.getWeekNumber(dateObj)
            const yearStr = String(dateObj.getFullYear())

            let dayIndex = dateObj.getDay() - 1
            if (dayIndex < 0) dayIndex = 6

            this.loading = true
            const recurrenceEnabled = !!(
                this.shiftForm.recurrence_end_date &&
                this.shiftForm.recurrence_days &&
                this.shiftForm.recurrence_days.length > 0
            )
            try {
                const response = await fetch(`/back-office/staff/${userId}/edit/planning_unique/${dayIndex}/${weekNumber}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                    body: JSON.stringify({
                        start,
                        end,
                        lunch_start,
                        lunch_end,
                        year: yearStr,
                        note: this.shiftForm.note || null,

                        frequency: recurrenceEnabled ? this.shiftForm.frequency : null,
                        recurrence_end_date: recurrenceEnabled ? this.shiftForm.recurrence_end_date : null,
                        recurrence_days: recurrenceEnabled ? this.shiftForm.recurrence_days : [],
                    }),
                })

                if (!response.ok) throw new Error('Failed to save shift')
                this.loadStaffResourcesPlanning()
                if (this.isEditingShift) {
                    this.$toast.success(this.$t('staff_planning_v3.main.shift_modified'))
                } else {
                    this.$toast.success(this.$t('staff_planning_v3.main.shift_created'))
                }
                this.showShiftModal = false
            } catch (error) {
                console.error(error)
                this.$toast.error(this.$t('staff_planning_v3.main.shift_create_error'))
            } finally {
                this.loading = false
            }
        },
        // Add 'modalData' as an argument to catch the emitted data from the modal
        async saveAbsence(modalData) {
            // 1. Prioritize modalData, fallback to absenceForm
            const formData = modalData || this.absenceForm

            // Find the correct staff based on the form values
            const selectedStaff = this.staffPlanning.find((s) => (s.user?.id || s.id) === formData.staff_id)

            // Calculate day index from formData.date
            const formDate = new Date(formData.date)
            const [year, weekStr] = this.selectedWeek.split('-W')
            let formDayIndex = -1

            for (let i = 0; i < 7; i++) {
                const dayDate = this.getDateFromWeek(parseInt(year), parseInt(weekStr), i)
                if (dayDate.toDateString() === formDate.toDateString()) {
                    formDayIndex = i
                    break
                }
            }

            let displayContent = formData.motif
            if (displayContent === 'Autre') {
                const firstPeriod = formData.periods?.[0] || { start: '00:00', end: '00:00' }
                displayContent = { start: firstPeriod.start, end: firstPeriod.end }
            }

            let statusNum = 5
            if (formData.motif === 'Maladie') statusNum = 1
            else if (formData.motif === 'Jour de vacances' || formData.motif === 'Congé payé') statusNum = 2
            else if (formData.motif === 'Repos') statusNum = 3

            const dateStr = formData.date
            const hasRecurrence = !!formData.frequency

            this.loading = true
            try {
                let isFirst = true
                const periods = formData.periods || []

                if (periods.length === 0) {
                    throw new Error('No periods defined')
                }

                for (const period of periods) {
                    const payload = {
                        user_id: formData.staff_id,
                        start: `${dateStr} ${period.start}:00`,
                        end: `${dateStr} ${period.end}:00`,
                        status: statusNum,
                        text: formData.comment,
                        textIndispo: formData.comment,
                        frequency: hasRecurrence ? formData.frequency : null,
                        recurrence_end_date: hasRecurrence ? formData.recurrence_end_date : null,
                        recurrence_days: hasRecurrence ? formData.selectedDays || [] : [],
                    }

                    let url = '/back-office/v3/planning/staff/exception/create/calendar'
                    if (this.isEditingAbsence) {
                        url = `/back-office/v3/planning/staff/exception/edit/calendar/${formData.id}`
                    }

                    const response = await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                            'X-Requested-With': 'XMLHttpRequest',
                        },
                        body: JSON.stringify(payload),
                    })

                    if (!response.ok) throw new Error('Failed to save absence')
                    isFirst = false
                }
                if (this.isEditingAbsence) {
                    this.$toast.success(this.$t('staff_planning_v3.main.absence_modified'))
                } else {
                    this.$toast.success(this.$t('staff_planning_v3.main.absence_saved'))
                }
                this.showAbsenceModal = false
                await this.loadStaffResourcesPlanning() // Assuming this method reloads the data
            } catch (error) {
                console.error('Error saving absence:', error)
                this.$toast.error(this.$t('staff_planning_v3.main.absence_error'))
            } finally {
                this.loading = false
            }
        },
        async prepareClone() {
            // Generate available weeks and open the same CopyPlanningModal as copy week feature
            // Set flag to show source staff selection since we're not copying from a specific row
            this.copyFromDuplicate = true
            await this.generatedPersonalizedWeeks()
            this.showCopyModal = true
        },
        async publishPlanning() {
            try {
                this.loading = true
                const response = await fetch('/back-office/v3/planning/staffs/publish', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                    body: JSON.stringify({
                        week: this.selectedWeek,
                    }),
                })

                if (!response.ok) {
                    throw new Error('Network response was not ok')
                }

                const data = await response.json()
                if (data.success) {
                    this.$toast.success(this.$t('staff_planning_v3.main.planning_published'))
                    await this.loadStaffResourcesPlanning()
                } else {
                    this.$toast.error(data.message || this.$t('staff_planning_v3.main.publish_error'))
                }
            } catch (error) {
                console.error('Error publishing planning:', error)
                this.$toast.error(this.$t('staff_planning_v3.main.publish_error'))
            } finally {
                this.loading = false
            }
        },
        async sendPlanning() {
            const [year, weekStr] = this.selectedWeek.split('-W')
            const { start, end } = this.getSartEndByYearWeek(year, weekStr)
            const weeksToExport =
                this.sendForm.weeks && this.sendForm.weeks.length > 0
                    ? this.sendForm.weeks
                    : [
                          {
                              label: `${this.$t('staff_planning_v3.components.create_shift.week')} ${weekStr}`,
                              range: `${this.formatDate(start)} - ${this.formatDate(end)}`,
                              value: this.selectedWeek,
                              disabled: false,
                          },
                      ]

            try {
                this.loading = true

                const response = await fetch('/back-office/v3/planning/staffs/send', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                    body: JSON.stringify({
                        week_number: this.selectedWeek,
                        format: this.sendForm.type,
                        staff_ids: this.sendForm.staffs,
                        note: this.sendForm.note,
                        weeks: weeksToExport,
                    }),
                })

                this.showSendModal = false
                this.sendForm.note = ''
                this.sendForm.staffs = []
                this.sendForm.weeks = []

                this.$toast.success(this.$t('staff_planning_v3.main.planning_sent'))
            } catch (error) {
                console.error('Error downloading planning:', error)
                this.$toast.error(this.$t('staff_planning_v3.main.send_error'))
            } finally {
                this.loading = false
            }
        },
        async handleSendPlanning() {
            await this.fetchPublishedWeeks()
            this.showSendModal = true
        },
        async cloneWeek() {
            if (this.cloneSelectedStaffs.length === 0) {
                this.$toast.error(this.$t('staff_planning_v3.main.select_employee'))
                return
            }

            try {
                this.loading = true
                const response = await fetch('/back-office/staff/planning/clone', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                    body: JSON.stringify({
                        source_week: this.cloneSourceWeek,
                        target_week: this.selectedWeek,
                        staff_ids: this.cloneSelectedStaffs,
                    }),
                })

                if (!response.ok) {
                    throw new Error('Failed to clone planning')
                }

                this.$toast.success(this.$t('staff_planning_v3.main.planning_duplicated'))

                // Reload planning data
                await this.loadStaffResourcesPlanning()

                // Close modal
                const modal = document.querySelector('#modal-clone')
                if (modal) {
                    bootstrap.Modal.getInstance(modal)?.hide()
                }
            } catch (error) {
                console.error('Error cloning planning:', error)
                this.$toast.error(this.$t('staff_planning_v3.main.duplicate_error'))
            } finally {
                this.loading = false
            }
        },
        async handleExport() {
            await this.fetchPublishedWeeks()
            this.showDownloadModal = true
            const [year, weekStr] = this.selectedWeek.split('-W')
            this.downloadForm.weekStr = `${this.$t('staff_planning_v3.components.create_shift.week')} ${weekStr}`
        },
        async downloadPlanning() {
            const [year, weekStr] = this.selectedWeek.split('-W')
            const { start, end } = this.getSartEndByYearWeek(year, weekStr)

            try {
                this.loading = true

                const weeksToExport =
                    this.downloadForm.weeks && this.downloadForm.weeks.length > 0
                        ? this.downloadForm.weeks
                        : [
                              {
                                  label: `${this.$t('staff_planning_v3.components.create_shift.week')} ${weekStr}`,
                                  range: `${this.formatDate(start)} - ${this.formatDate(end)}`,
                                  value: this.selectedWeek,
                                  disabled: false,
                              },
                          ]

                const response = await fetch('/back-office/v3/planning/staffs/export', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                    body: JSON.stringify({
                        week_number: this.selectedWeek,
                        format: this.downloadForm.type,
                        staff_ids: this.downloadForm.staffs,
                        note: this.downloadForm.note,
                        weeks: weeksToExport,
                    }),
                })

                // Extract filename from Content-Disposition header
                const contentDisposition = response.headers.get('Content-Disposition')
                let filename = 'planning.xlsx'

                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
                    if (filenameMatch) {
                        filename = filenameMatch[1].replace(/['"]/g, '')
                    }
                }

                // Create download link and trigger download
                const blob = await response.blob()
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', filename)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)

                // Close modal
                this.showDownloadModal = false
                this.downloadForm.note = ''
                this.downloadForm.staffs = []
                this.downloadForm.weeks = []

                this.$toast.success(this.$t('staff_planning_v3.main.planning_downloaded'))
            } catch (error) {
                console.error('Error downloading planning:', error)
                this.$toast.error(this.$t('staff_planning_v3.main.download_error'))
            } finally {
                this.loading = false
            }
        },
        goToCreateStaffPage() {
            window.location.href = '/back-office/v3/staffs/create'
        },
        getSartEndByYearWeek(year, weekStr) {
            const yearNum = parseInt(year, 10)
            const weekNum = parseInt(weekStr, 10)

            // 1. Find January 4th of that year (ISO Week 1 always contains Jan 4)
            const jan4 = new Date(yearNum, 0, 4)

            // 2. Find the Monday of that first ISO week
            // jan4.getDay() returns 0 for Sunday, 1 for Monday, etc.
            const dayOfWeek = jan4.getDay() || 7 // Convert Sunday from 0 to 7
            const mondayOfWeek1 = new Date(jan4)
            mondayOfWeek1.setDate(jan4.getDate() - dayOfWeek + 1)

            // 3. Add the required number of weeks to find our target Monday
            const start = new Date(mondayOfWeek1)
            start.setDate(mondayOfWeek1.getDate() + (weekNum - 1) * 7)

            // 4. Add 6 days to get the Sunday
            const end = new Date(start)
            end.setDate(start.getDate() + 6)

            return { start, end }
        },
        getWeekRange(date) {
            const start = new Date(date)
            const day = start.getDay()

            // move to Monday
            const diffToMonday = start.getDate() - ((day + 6) % 7)
            start.setDate(diffToMonday)

            const end = new Date(start)
            end.setDate(start.getDate() + 6)

            return { start, end }
        },

        formatDate(date) {
            const d = new Date(date)

            const day = String(d.getDate()).padStart(2, '0')
            const month = d.toLocaleString('fr-FR', { month: 'short' })

            return `${day} ${month}`
        },

        getISOWeekNumber(date) {
            const temp = new Date(date.getTime())
            temp.setHours(0, 0, 0, 0)
            temp.setDate(temp.getDate() + 3 - ((temp.getDay() + 6) % 7))

            const week1 = new Date(temp.getFullYear(), 0, 4)

            return 1 + Math.round(((temp - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
        },
        generateNextMonthWeeks() {
            // Show published upcoming weeks (those that appear in publishedUpcomingWeeks)
            // Scoped to the next 2 months as a window, marked disabled if not published
            const now = new Date()

            const firstDay = new Date(now.getFullYear(), now.getMonth() + 1, 1)
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 2, 0)

            const weeks = []
            const seen = new Set()

            let cursor = new Date(firstDay)

            while (cursor <= lastDay) {
                const { start } = this.getWeekRange(cursor)

                const weekNumber = this.getISOWeekNumber(start)
                const weekYear = start.getFullYear()
                const weekValue = weekYear + '-W' + String(weekNumber).padStart(2, '0')

                const key = start.toISOString()

                if (!seen.has(key)) {
                    seen.add(key)

                    const end = new Date(start)
                    end.setDate(start.getDate() + 6)

                    const isPublished = this.publishedUpcomingWeeks.includes(weekValue)

                    weeks.push({
                        label: `${this.$t('staff_planning_v3.components.create_shift.week')} ${weekNumber}`,
                        range: `${this.formatDate(start)} - ${this.formatDate(end)}`,
                        value: weekValue,
                        disabled: !isPublished,
                    })
                }

                cursor.setDate(cursor.getDate() + 1)
            }
            this.nextMonthWeeks = weeks
            this.enabledWeeksMonthCount = weeks.filter((week) => !week.disabled).length
        },
        generatedPersonalizedWeeks() {
            // Show all upcoming weeks for the next 12 months,
            // but mark unpublished ones as disabled
            const now = new Date()

            const firstDay = new Date(now.getFullYear(), now.getMonth() + 1, 1)
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 12, 0)

            const weeks = []
            const seen = new Set()

            let cursor = new Date(firstDay)

            while (cursor <= lastDay) {
                const { start } = this.getWeekRange(cursor)

                const weekNumber = this.getISOWeekNumber(start)
                const weekYear = start.getFullYear()
                const weekValue = weekYear + '-W' + String(weekNumber).padStart(2, '0')

                const key = start.toISOString()

                if (!seen.has(key)) {
                    seen.add(key)

                    const end = new Date(start)
                    end.setDate(start.getDate() + 6)

                    const isPublished = this.publishedUpcomingWeeks.includes(weekValue)

                    weeks.push({
                        label: `${this.$t('staff_planning_v3.components.create_shift.week')} ${weekNumber}`,
                        range: `${this.formatDate(start)} - ${this.formatDate(end)}`,
                        value: weekValue,
                        disabled: !isPublished,
                    })
                }

                cursor.setDate(cursor.getDate() + 1)
            }
            this.personalizedWeeks = weeks
            this.enabledWeeksPersoCount = weeks.filter((week) => !week.disabled).length
        },
        async fetchPublishedWeeks() {
            try {
                const response = await fetch('/back-office/v3/planning/staffs/published-weeks', {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                })
                if (!response.ok) throw new Error('Failed to fetch published weeks')
                const data = await response.json()
                this.publishedUpcomingWeeks = data.weeks || []
            } catch (error) {
                console.error('Error fetching published weeks:', error)
                this.publishedUpcomingWeeks = []
            }
        },
        getDateOfISOWeek(week, year) {
            const simple = new Date(year, 0, 1 + (week - 1) * 7)

            const dayOfWeek = simple.getDay()

            const ISOweekStart = new Date(simple)

            if (dayOfWeek <= 4) {
                ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1)
            } else {
                ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay())
            }

            return ISOweekStart
        },
        async fetchMonthStaffTotals(formType) {
            this.loading = true
            const form = formType === 'download' ? this.downloadForm : this.sendForm

            const params = new URLSearchParams()

            form.weeks.forEach((week) => {
                params.append('week_number[]', week.value)
            })
            try {
                const response = await fetch(`/back-office/v3/planning/staff_resources?${params.toString()}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                })

                if (!response.ok) {
                    throw new Error('Failed to fetch staff data')
                }

                const data = await response.json()
                this.staffPlanning = data.planning || []
                this.downloadStaffTotals = this.staffResouerceList
            } catch (error) {
                this.$toast.error(this.$t('staff_planning_v3.main.load_hours_error'))
            } finally {
                this.loading = false
            }
        },
        handleTypeChange(val, source) {
            const form = source === 'download' ? this.downloadForm : this.sendForm

            if (val === 'month') {
                form.weeks = []
                this.generateNextMonthWeeks(form)
                form.weekStr = ''
            } else if (val === 'week') {
                const [year, weekStr] = this.selectedWeek.split('-W')

                form.weekStr = `${this.$t('staff_planning_v3.components.create_shift.week')} ${weekStr}`

                let start = this.getDateOfISOWeek(weekStr, year)
                let end = new Date(start)
                end.setDate(end.getDate() + 6)

                let weekNumber = this.getISOWeekNumber(start)

                form.weeks = [
                    {
                        label: `${this.$t('staff_planning_v3.components.create_shift.week')} ${weekNumber}`,
                        range: `${this.formatDate(start)} - ${this.formatDate(end)}`,
                        value: `${start.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`,
                    },
                ]
            } else {
                form.weekStr = ''
                this.generatedPersonalizedWeeks(form)
            }
        },
        async handleWeeksChange(val, formType) {
            const form = formType === 'download' ? this.downloadForm : this.sendForm

            if (['month', 'custom', 'week'].includes(form.type) && val?.length) {
                await this.fetchMonthStaffTotals(formType)
            }
        },
        modalStaffList(formType) {
            const form = formType === 'download' ? this.downloadForm : this.sendForm

            if (formType === 'copy') {
                return this.staffResouerceList
            }

            return ['month', 'custom'].includes(form.type) && form.weeks.length > 0
                ? this.downloadStaffTotals
                : this.staffResouerceList
        },
        copyWeek() {
            if (!this.targetRow) return
            this.copyModalInitialStaffId = this.targetRow.user?.id || this.targetRow.uid
            this.generatedPersonalizedWeeks() // Make sure we have the upcoming weeks list
            this.showCopyModal = true
        },
        onCopyModalConfirm(data) {
            this.copyModalPendingData = data
            this.showCopyModal = false
            this.showCopyOverwriteConfirmModal = true
        },
        async executeCopyMultiple() {
            this.showCopyOverwriteConfirmModal = false
            if (!this.copyModalPendingData) return

            try {
                this.loading = true
                // Use sourceStaffId from modal if copying from Dupliquer button, otherwise use targetRow
                const sourceStaffId = this.copyFromDuplicate
                    ? this.copyModalPendingData.sourceStaffId
                    : this.targetRow?.user?.id || this.targetRow?.uid || null

                if (!sourceStaffId) {
                    this.$toast.error(this.$t('staff_planning_v3.main.source_undetermined'))
                    return
                }

                const response = await fetch('/back-office/v3/planning/staffs/copy_multiple', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({
                        source_staff_id: sourceStaffId,
                        source_week: this.selectedWeek,
                        target_staff_id: this.copyModalPendingData.targetStaffId,
                        target_weeks: this.copyModalPendingData.targetWeeks.map((w) => (typeof w === 'object' ? w.value : w)),
                        overwrite: true,
                    }),
                })

                if (!response.ok) {
                    throw new Error('Failed to copy planning')
                }

                const data = await response.json()
                if (data.success) {
                    this.$toast.success(this.$t('staff_planning_v3.main.planning_duplicated'))
                    await this.loadStaffResourcesPlanning()
                } else {
                    this.$toast.error(data.message || this.$t('common.copy_error'))
                }
            } catch (error) {
                console.error('Error copying planning:', error)
                this.$toast.error(this.$t('staff_planning_v3.main.copy_error'))
            } finally {
                this.loading = false
                this.copyModalPendingData = null
                this.copyFromDuplicate = false
            }
        },

        async sendWeek() {
            if (!this.targetRow) return

            const staffId = this.targetRow.user?.id || this.targetRow.uid
            const staffName = this.targetRow.user
                ? `${this.targetRow.user.first_name} ${this.targetRow.user.last_name}`
                : this.targetRow.name

            if (!staffId) {
                this.$toast.error(this.$t('staff_planning_v3.main.staff_undetermined'))
                return
            }

            this.loading = true
            try {
                const response = await fetch('/back-office/v3/planning/staffs/send', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                        'X-Requested-With': 'XMLHttpRequest',
                        Authorization: 'Bearer ' + this.token,
                    },
                    body: JSON.stringify({
                        staff_ids: [staffId],
                        weeks: [{ value: this.selectedWeek }],
                        note: '',
                    }),
                })

                const data = await response.json()

                if (!response.ok) {
                    throw new Error(data.message || "Erreur lors de l'envoi")
                }

                this.$toast.success(this.$t('staff.planning_sent_email', { name: staffName }))
            } catch (error) {
                console.error('Error sending week planning:', error)
                this.$toast.error(error.message || this.$t('staff.planning_send_error'))
            } finally {
                this.loading = false
                this.targetRow = null
            }
        },

        deleteWeek() {
            this.showConfirmDeleteWeek = true
        },
        openResourceModal() {
            this.isEditingResource = false
            this.selectedResource = { name: '', email: '', uid: '' }
            this.resourceErrors = {}
            this.showResourceExterneModal = true
        },
        async handleSaveResource(formData) {
            this.resourceExternes.push(formData)
            try {
                const response = await fetch(`/back-office/staff/resource_externe/create`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                    body: JSON.stringify(formData),
                })

                const data = await response.json()

                if (!response.ok) {
                    if (response.status === 422) {
                        this.resourceErrors = data.errors
                    }
                    return
                }
                this.$toast.success(this.$t('staff_planning_v3.main.shift_created'))
                this.isEditingResource = false
                this.showResourceExterneModal = false
                await this.loadStaffResourcesPlanning()
            } catch (error) {
                console.error(error)
                this.$toast.error(this.$t('staff_planning_v3.main.shift_create_error'))
            } finally {
                this.loading = false
            }
        },
        editResource() {
            if (!this.targetRow) return
            this.isEditingResource = true
            this.selectedResource = {
                id: this.targetRow.id,
                name: this.targetRow.name,
                email: this.targetRow.email,
                uid: this.targetRow.uid,
            }
            this.resourceErrors = {}
            this.showResourceExterneModal = true
        },
        async handleUpdateResource(formData) {
            try {
                const response = await fetch(`/back-office/staff/resource_externe/${formData.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                    body: JSON.stringify(formData),
                })

                const data = await response.json()

                if (!response.ok) {
                    if (response.status === 422) {
                        this.resourceErrors = data.errors
                    }
                    return
                }
                this.$toast.success(this.$t('staff_planning_v3.main.resource_updated'))
                this.isEditingResource = false
                this.showResourceExterneModal = false
                await this.loadStaffResourcesPlanning()
            } catch (error) {
                console.error(error)
                this.$toast.error(this.$t('staff_planning_v3.main.resource_update_error'))
            } finally {
                this.loading = false
            }
        },
        requestDeleteResource() {
            if (!this.targetRow) return
            this.resourceToDelete = this.targetRow
            this.confirmDeleteResourceModal = true
        },
        async confirmDeleteResource() {
            if (!this.resourceToDelete) return
            this.confirmDeleteResourceModal = false
            this.loading = true
            try {
                const response = await fetch(`/back-office/staff/resource_externe/${this.resourceToDelete.id}`, {
                    method: 'DELETE',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    },
                })

                if (!response.ok) {
                    throw new Error('Failed to delete resource')
                }

                this.$toast.success(this.$t('staff_planning_v3.main.resource_deleted'))
                await this.loadStaffResourcesPlanning()
            } catch (error) {
                console.error(error)
                this.$toast.error(this.$t('staff_planning_v3.main.resource_delete_error'))
            } finally {
                this.loading = false
                this.resourceToDelete = null
            }
        },
        onMenuSelect(action, staffOrResource) {
            this.targetRow = staffOrResource
        },
        async confirmDeleteWeek() {
            this.showConfirmDeleteWeek = false
            try {
                await axios.delete(
                    `/back-office/v3/planning/by/staffs/${
                        this.targetRow.user?.id ? this.targetRow.user.id : this.targetRow.uid
                    }/week/${this.selectedWeek}`
                )
            } catch (error) {
                console.error('Failed to delete planning:', error)
                // Optional: Alert the user that the deletion failed
            } finally {
                // 4. Always clean up your temporary targets so they don't leak into the next action
                this.targetRow = null
                await this.loadStaffResourcesPlanning()
            }
        },
    },
}
</script>

<style scoped>
table {
    border-collapse: collapse;
}

/* Hide scrollbar for clean design but allow scrolling */
.ppv2-overflow-x-auto::-webkit-scrollbar {
    height: 8px;
}
.ppv2-overflow-x-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}
.ppv2-overflow-x-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}
.ppv2-overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
table:not([cellpadding]) td {
    padding: 0px !important;
}
</style>
